import { EnvelopeSimple, Robot, MapTrifold, ChatCircleDots, ReceiptX, ArrowRight, CaretDown } from "@phosphor-icons/react";

const STEPS = [
  {
    icon: EnvelopeSimple,
    label: "Broker Email Arrives",
    detail: "Pasted or auto-forwarded — the rate confirmation lands in FreightPilot.",
    color: "text-blue-400",
    ring: "ring-blue-400/30 bg-blue-400/[0.06]",
  },
  {
    icon: Robot,
    label: "AI Reads the Load",
    detail: "Extracts origin, destination, rate, pickup/delivery, weight, notes — in 6 seconds.",
    color: "text-purple-400",
    ring: "ring-purple-400/30 bg-purple-400/[0.06]",
  },
  {
    icon: MapTrifold,
    label: "Dispatched to Driver",
    detail: "Auto-assigned + SMS sent to the driver with pickup address and load notes.",
    color: "text-emerald-400",
    ring: "ring-emerald-400/30 bg-emerald-400/[0.06]",
  },
  {
    icon: ChatCircleDots,
    label: "Customer Updates",
    detail: "Status changes trigger auto-emails to the broker: picked up, in transit, delivered.",
    color: "text-cyan-400",
    ring: "ring-cyan-400/30 bg-cyan-400/[0.06]",
  },
  {
    icon: ReceiptX,
    label: "Invoice Generated",
    detail: "PDF invoice + rate confirmation + BOL emailed for payment. You just approve.",
    color: "text-[#FF5A1F]",
    ring: "ring-[#FF5A1F]/40 bg-[#FF5A1F]/[0.08]",
  },
];

export default function WorkflowDiagram() {
  return (
    <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-28"
             data-testid="workflow-diagram">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <div className="inline-flex items-center gap-2 pill pill-primary mb-4">
          <ArrowRight size={12} weight="bold" /> HOW IT WORKS
        </div>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-tight">
          From broker email to paid invoice
          <br />
          <span className="text-[#FF5A1F]">without you touching the keyboard.</span>
        </h2>
        <p className="text-neutral-500 text-sm sm:text-base mt-5 max-w-xl mx-auto">
          Watch every load flow through the same 5 steps — automated end-to-end.
        </p>
      </div>

      {/* Desktop: horizontal flow */}
      <div className="hidden md:block">
        <div className="relative grid grid-cols-5 gap-3">
          {STEPS.map((s, i) => (
            <div key={i}
                 className="relative flex flex-col"
                 style={{ animation: `wf-fade-in 0.6s ${i * 0.15}s both` }}
                 data-testid={`workflow-step-${i}`}>
              <div className={`relative rounded-2xl border border-white/10 ${s.ring} ring-1 p-5 h-full`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-11 h-11 rounded-xl ${s.ring} ring-1 ring-inset flex items-center justify-center`}>
                    <s.icon size={22} weight="fill" className={s.color} />
                  </div>
                  <div className="text-[10px] font-mono text-neutral-600">0{i + 1}</div>
                </div>
                <div className={`text-sm font-semibold ${s.color} mb-1`}>{s.label}</div>
                <div className="text-xs text-neutral-400 leading-relaxed">{s.detail}</div>
              </div>
              {i < STEPS.length - 1 && (
                <div className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none"
                     style={{ animation: `wf-arrow-pulse 2s ${i * 0.4}s infinite ease-in-out` }}>
                  <div className="w-6 h-6 rounded-full bg-[#0A0A0A] border border-white/10 flex items-center justify-center">
                    <ArrowRight size={12} weight="bold" className="text-neutral-500" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: vertical flow */}
      <div className="md:hidden space-y-4">
        {STEPS.map((s, i) => (
          <div key={i}
               className="relative"
               style={{ animation: `wf-fade-in 0.6s ${i * 0.15}s both` }}
               data-testid={`workflow-step-mobile-${i}`}>
            <div className={`rounded-2xl border border-white/10 ${s.ring} ring-1 p-4`}>
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 shrink-0 rounded-xl ${s.ring} ring-1 ring-inset flex items-center justify-center`}>
                  <s.icon size={22} weight="fill" className={s.color} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-[10px] font-mono text-neutral-600">0{i + 1}</div>
                    <div className={`text-sm font-semibold ${s.color}`}>{s.label}</div>
                  </div>
                  <div className="text-xs text-neutral-400 leading-relaxed">{s.detail}</div>
                </div>
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex justify-center py-1 opacity-40">
                <CaretDown size={16} className="text-neutral-500" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Below-flow tagline */}
      <div className="text-center mt-12">
        <div className="inline-flex items-center gap-2 text-sm text-neutral-400">
          <div className="w-8 h-px bg-white/10" />
          Same 5 steps. Every load. No exceptions.
          <div className="w-8 h-px bg-white/10" />
        </div>
      </div>

      <style>{`
        @keyframes wf-fade-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes wf-arrow-pulse {
          0%, 100% { transform: translateY(-50%) translateX(0); opacity: 0.5; }
          50% { transform: translateY(-50%) translateX(4px); opacity: 1; }
        }
      `}</style>
    </section>
  );
}
